import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { Badge, BadgeType } from '../../components/badge';
import { Filters } from '../../components/filters';
import { useSearchReducer } from '../../components/hooks/useSearchReducer';
import { PageHeader } from '../../components/page-header';
import { CellProps, TableComponent } from '../../components/table';
import { Button } from '../../components/ui/button';
import { getRangeLength } from '../../components/utils/time';
import { Api } from '../../core/api';
import {
    ActivityRejectionReason,
    FacilityVisitType
} from '../../core/api/FitnessActivityClient';
import { I18n } from '../../core/i18n';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';
import {
    facilityVisitToItem,
    VerificationItem,
    VerificationType,
    workoutToItem
} from '../types';
import { VerificationDialog } from '../verification';

export enum WorkoutStatusType {
    NOT_VERIFIED = 'NOT VERIFIED',
    REJECTED = 'REJECTED',
    VERIFIED = 'VERIFIED'
}
export interface WorkoutStatus {
    badgeType: BadgeType;
    label: string;
    statusType: WorkoutStatusType;
}

export function getStatusBadgeType(item: {
    verificationRequired: boolean;
    rejectionReason?: ActivityRejectionReason;
}): WorkoutStatus {
    if (item.verificationRequired) {
        return {
            badgeType: 'yellow',
            label: 'Not Verified',
            statusType: WorkoutStatusType.NOT_VERIFIED
        };
    } else if (item.rejectionReason) {
        return {
            badgeType: 'red',
            label: 'Rejected',
            statusType: WorkoutStatusType.REJECTED
        };
    }
    return {
        badgeType: 'green',
        label: 'Verified',
        statusType: WorkoutStatusType.VERIFIED
    };
}

export enum StatusFiltersType {
    ALL = 'ALL',
    REJECTED = 'REJECTED',
    NOT_VERIFIED = 'NOT VERIFIED'
}

export const PrivatePlaceVerificationList = observer(() => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const { formatDateTime } = injectTSDI(I18n);

    const [statusFilter, setStatusFilter] = React.useState<StatusFiltersType>(
        StatusFiltersType.NOT_VERIFIED
    );
    const [typeFilter, setTypeFilter] = React.useState<VerificationType>(
        VerificationType.FACILITY_VISIT
    );

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });

    // Item currently shown in the verification popup, and bookkeeping to
    // refresh the list once the popup closes after any approve/reject.
    const [selectedId, setSelectedId] = React.useState<number | null>(null);
    const [reloadToken, setReloadToken] = React.useState(0);
    const dirty = React.useRef(false);

    const items = useWrapRequest(
        async (searchParams, statusFilter, typeFilter, _reloadToken) => {
            const query = {
                ...searchParams,
                ...(statusFilter === StatusFiltersType.NOT_VERIFIED && {
                    verificationRequired: true
                }),
                ...(statusFilter === StatusFiltersType.REJECTED && {
                    rejected: true
                }),
                sort: '-timestamp'
            };

            if (typeFilter === VerificationType.WORKOUT) {
                const result =
                    await fitnessActivityClient.admin.workoutSupportControllerGetPage(
                        query
                    );

                return {
                    ...result.data,
                    content: (result.data.content || []).map(workoutToItem)
                };
            }

            const result =
                await fitnessActivityClient.admin.facilityVisitSupportControllerGetPage(
                    {
                        ...query,
                        visitType: FacilityVisitType.PRIVATE_FACILITY
                    }
                );

            return {
                ...result.data,
                content: (result.data.content || []).map(facilityVisitToItem)
            };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: 0,
                totalPages: 0,
                totalElements: 0
            },
            deps: [searchParams, statusFilter, typeFilter, reloadToken]
        }
    );

    const content = items.$?.content ?? [];
    const selectedItem = content.find((item) => item.id === selectedId);

    const handleClose = () => {
        setSelectedId(null);
        if (dirty.current) {
            dirty.current = false;
            setReloadToken((token) => token + 1);
        }
    };

    const handleNext = () => {
        const index = content.findIndex((item) => item.id === selectedId);
        const next = index >= 0 ? content[index + 1] : undefined;
        if (next) {
            setSelectedId(next.id);
        } else {
            handleClose();
        }
    };

    const handlePrevious = () => {
        const index = content.findIndex((item) => item.id === selectedId);
        const previous = index > 0 ? content[index - 1] : undefined;
        if (previous) {
            setSelectedId(previous.id);
        }
    };

    return (
        <>
            <PageHeader title="Private workout Verification" />

            <div className="mb-4 flex flex-wrap items-end gap-3">
                <div className="w-[180px]">
                    <Filters<VerificationType, false>
                        label="Type"
                        value={typeFilter}
                        options={[
                            {
                                label: 'Facility Visit',
                                value: VerificationType.FACILITY_VISIT
                            },
                            {
                                label: 'Workout',
                                value: VerificationType.WORKOUT
                            }
                        ]}
                        onChange={(val) => void setTypeFilter(val)}
                    />
                </div>
                <div className="w-[180px]">
                    <Filters<StatusFiltersType, false>
                        label="Status"
                        value={statusFilter}
                        options={[
                            { label: 'All', value: StatusFiltersType.ALL },
                            {
                                label: 'Not Verified',
                                value: StatusFiltersType.NOT_VERIFIED
                            },
                            {
                                label: 'Rejected',
                                value: StatusFiltersType.REJECTED
                            }
                        ]}
                        onChange={(val) => void setStatusFilter(val)}
                    />
                </div>
            </div>

            <TableComponent<VerificationItem[]>
                emptyText="No entries"
                items={items}
                columns={[
                    {
                        Header: 'Workout Id',
                        accessor: (value) => value.id,
                        Cell: ({
                            row: { original }
                        }: CellProps<VerificationItem>) => (
                            <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0"
                                onClick={() => setSelectedId(original.id)}
                            >
                                {original.id}
                            </Button>
                        ),
                        style: { width: 140 }
                    },
                    {
                        Header: 'User Id',
                        Cell: ({
                            row: { original }
                        }: CellProps<VerificationItem>) => (
                            <Link
                                className="text-primary hover:underline"
                                to={links.userDetails(original.userId)}
                            >
                                {original.userId}
                            </Link>
                        ),

                        style: { width: 100 }
                    },
                    {
                        Header: 'Date',
                        Cell: ({
                            row: { original }
                        }: CellProps<VerificationItem>) => (
                            <span className="text-sm">
                                {formatDateTime(
                                    original.zonedDateTimeRangeModel.from
                                )}
                            </span>
                        ),
                        style: { width: 200 }
                    },
                    {
                        Header: 'Workout Length',
                        Cell: ({
                            row: { original }
                        }: CellProps<VerificationItem>) => (
                            <span className="text-sm">
                                {getRangeLength(
                                    original.zonedDateTimeRangeModel
                                )}{' '}
                                min
                            </span>
                        )
                    },
                    {
                        Header: 'Rejection Reason',
                        accessor: (value) => value.rejectionReason
                    },
                    {
                        Header: 'Status',
                        Cell: ({
                            row: { original }
                        }: CellProps<VerificationItem>) => {
                            const status = getStatusBadgeType(original);
                            return (
                                <Badge type={status.badgeType}>
                                    {status.label}
                                </Badge>
                            );
                        }
                    },
                    {
                        Header: '',
                        accessor: 'id',
                        Cell: ({
                            row: { original }
                        }: CellProps<VerificationItem>) => (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedId(original.id)}
                            >
                                Modify
                            </Button>
                        ),
                        style: { textAlign: 'right' }
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => void dispatch({ type: 'page', page })}
            />
            <VerificationDialog
                id={selectedId ?? undefined}
                type={selectedItem?.type}
                open={selectedId != null}
                onClose={handleClose}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onDirty={() => {
                    dirty.current = true;
                }}
            />
        </>
    );
});

export default PrivatePlaceVerificationList;
